import { HttpParams } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  AnalyticsService,
  DropdownItem,
  PaginationComponent,
  ResponsiveService,
  ToggleButton,
} from '@frk/eds-components';
import { getMonthDiffrence } from '@marketing/marketing-utils';
import { FtInsightsItem } from '@search/interfaces/insights.interface';
import {
  FtSearchOptions,
  FtSearchParams,
} from '@search/interfaces/search.interface';
import { ConfigService } from '@search/services/config.service';
import { FiltersService } from '@search/services/filters.service';
import { FtSearchService } from '@search/services/ftsearch.service';
import {
  AppStateService,
  DebugService,
  GlobalConfigService,
  SegmentService,
  WidenService,
  WindowScrollService,
} from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { SegmentId, SiteSearchConfig } from '@types';
import { Logger } from '@utils/logger';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DisplayOptions, FiltersData } from '../insights.interface';
import { InsightsService } from '../insights.service';
const logger = Logger.getLogger('InsightsLibraryComponent');

@Component({
  selector: 'ft-insights-library',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './insights-library.component.html',
  styleUrls: ['./insights-library.component.scss'],
  providers: [InsightsService, FtSearchService],
})
export class InsightsLibraryComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  isMobile$: Observable<boolean>;
  isHandheld: boolean;
  private currentValue = 'y';
  @Input() externalTranslationLabel: any;
  @Input() hideLibraryHeader: boolean;
  @Input() preFilter: any;
  @Input() maximumPageSize?: number;
  @Input() compactImage?: boolean;
  @Input() set current(value: string) {
    this.currentValue = value;
  }

  get current() {
    return this.currentValue;
  }

  /**
   * SIM logo resource bundle object from parent component
   */
  @Input() externalSIMLogoResource: object;

  debouncer$: Subject<FtSearchParams> = new Subject<FtSearchParams>();

  displayOptions: DisplayOptions;
  visibleRecords: number;
  totalRecords: number;
  translationLabel: any;
  filtersData: FiltersData = {};
  selectedTab = 'listView';
  displayedPage = 0;
  aggregations = {};
  error = false;
  componentDestroyed$: Subject<boolean> = new Subject();
  searchTerm = '';
  results: FtInsightsItem[] = [];
  activeFilters: string[];
  selectedArticleType: string;
  filtersAvailable: boolean;
  totalPages = 0;
  spaBaseUrl: string;
  sideBarDocumentRef: any;
  isDebugMode = false;
  itemsPerPageOption: DropdownItem[] = [];
  pageSize = {
    value: 8,
    label: '8',
  };
  options: FtSearchOptions = {
    page: 0,
    term: '*',
    dataSource: 'dev',
    articleType: 'insight',
    current: '',
    config: {
      ftSearchUrl: '',
      ftInsightsUrl: '',
      httpParams: new HttpParams(),
      httpOptions: {},
    },
    // TODO verify if we use it and move to config
    counters: {
      funds: 4,
      literature: 4,
      pages: this.pageSize?.value,
    },
  };
  query = '*';
  searchDebounceTime: number;
  searchConfig: SiteSearchConfig;
  @Input() hideSidebars = false;

  sideBarLeft: boolean;
  colNumbersTwo: boolean;
  colNumbersThree: boolean;
  public isLoading = true;

  /**
   * SIM logo resource bundle object
   */
  simLogoResourceBundle: object;

  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  pageTitle: string;
  currentSegment: SegmentId;
  constructor(
    private responsiveService: ResponsiveService,
    private filters: FiltersService,
    private cd: ChangeDetectorRef,
    private configService: ConfigService,
    private searchService: FtSearchService,
    private widenService: WidenService,
    private appStateService: AppStateService,
    private insightsService: InsightsService,
    private analytics: AnalyticsService,
    private titleService: Title,
    private segmentService: SegmentService,
    private debugService: DebugService,
    private scrollService: WindowScrollService,
    private pageConfigService: GlobalConfigService
  ) {
    super();
    combineLatest([
      this.searchService.insightsOutput$,
      this.segmentService.getCurrentSegmentId$(),
    ])
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        ([{ results }, currentSegmentId]) => {
          this.isLoading = false;
          logger.debug(results);
          this.currentSegment = currentSegmentId;
          this.results = results.response?.hits?.hits;
          this.results.forEach((element, index: number) => {
            if (element._source?.referenceDate) {
              if (this.options.current === 'y') {
                element._source.isArchived = false;
              } else if (this.options.current === 'n') {
                element._source.isArchived = true;
              } else {
                if (
                  getMonthDiffrence(element._source?.referenceDate) > 15 &&
                  !element._source?.doNotArchive
                ) {
                  element._source.isArchived = true;
                }
              }

              element._source.referenceDate = this.insightsService.getLongDate(
                element._source.referenceDate
              );
            }
            if (element._source?.image) {
              element._source.imgSrc = this.getImageUrl(element._source?.image);
            }
            if (element._source?.cardSummary) {
              element._source.description = this.truncateString(
                element._source?.cardSummary
              );
            }

            if (element?._source?.logo) {
              element._source.SIM = this.insightsService.getSIMLogo(
                element?._source?.logo,
                true,
                this.simLogoResourceBundle
              );
            }
            element._source.relativePath = this.configService.getLocalizedPath(
              element._source.relativePath
            );
          });
          this.aggregations = results.response.aggregations;
          this.visibleRecords = this.results.length;
          this.totalRecords = results.response?.hits?.total?.value;
          this.totalPages = Math.ceil(
            results.response?.hits?.total?.value / this.options.counters.pages
          );
          this.paginationComponent?.updatePagination(this.totalPages);
          this.cd.detectChanges();
        },
        (error) => {
          // this.searchService.loading$.next(false);
        }
      );

    this.searchConfig = this.pageConfigService.getSearchConfig();
    this.searchDebounceTime = this?.searchConfig?.searchDebounceTime || 500;

    // ignore search term if new is called within searchDebounceTime which is set from BR
    // and default value is 500ms
    // debouncer is triggered after search term
    this.debouncer$
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(this.searchDebounceTime)
      )
      .subscribe((params: FtSearchParams) => {
        this.searchService.getInsights(params);
      });
  }

  ngOnInit(): void {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.responsiveService.isHandheld$()?.subscribe((isTablet: boolean) => {
      this.isHandheld = isTablet;
    });
    this.activeFilters = this.filters.activeFilters;
    this.translationLabel = this.getTranslationLabels();
    this.pageTitle = this.titleService.getTitle();
    logger.debug(this.translationLabel);
    this.displayOptions = this.component?.getParameters();
    this.spaBaseUrl = this.appStateService?.getSpaBaseUrl();
    this.selectedArticleType = this.translationLabel?.currentArticles;
    this.createDropDownRowNoItems();

    if (!this.externalSIMLogoResource) {
      this.simLogoResourceBundle = this.component?.getModels()[
        'ft.core.sim-logos'
      ];
    } else {
      this.simLogoResourceBundle = this.externalSIMLogoResource;
    }

    this.filtersData = {
      items: [
        {
          link: this.translationLabel?.allArticles,
        },
        {
          link: this.translationLabel?.currentArticles,
        },
        {
          link: this.translationLabel?.archivedArticles,
        },
      ],
    };

    if (this.translationLabel?.query) {
      this.query = this.translationLabel?.query;
    }

    if (this.filtersData) {
      this.filtersData.toggleButtons = [];
    }

    if (this.displayOptions?.enableListView || this.hideLibraryHeader) {
      this.filtersData?.toggleButtons.push({
        value: 'listView',
        text: this.translationLabel?.listView,
        active: false,
      });
    }

    if (this.displayOptions?.enableGridView || this.hideLibraryHeader) {
      this.filtersData?.toggleButtons.push({
        value: 'gridView',
        text: this.translationLabel?.gridView,
        active: false,
      });
    }

    if (
      this.displayOptions?.enableGridView &&
      !this.displayOptions.enableListView
    ) {
      this.selectedTab = 'gridView';
    }

    if (this.filtersData?.toggleButtons[0]) {
      this.filtersData.toggleButtons[0].active = true;
    }

    if (this.component?.getModels()?.document) {
      this.sideBarDocumentRef = this.page
        .getContent(this.component?.getModels()?.document)
        ?.getData();
      this.sideBarLeft =
        this.sideBarDocumentRef?.collection?.length > 0 ||
        this.sideBarDocumentRef?.collections?.length > 0;
      if (!this.sideBarLeft) {
        this.colNumbersTwo = false;
        this.colNumbersThree = false;
      } else {
        this.colNumbersTwo = true;
        this.colNumbersThree = true;
      }
    }
    this.options.current = this.currentValue;

    if (this.maximumPageSize) {
      this.options.counters.pages = this.maximumPageSize;
    }

    this.configService
      .getConfig('insights')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((config) => {
        const insightAudiences = this.segmentService.getCurrentSegment()
          ?.insightAudiences;
        config.httpParams = config.httpParams.set(
          'exclude',
          insightAudiences ? insightAudiences : ''
        );
        this.options.config = config;
        this.setPreFilter();
        this.termSearch(this.query);
      });

    this.debugService
      .isShowInsightsReport$()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((isShowInsightsReport: boolean) => {
        this.isDebugMode = isShowInsightsReport;
        this.cd.detectChanges();
      });
  }

  /**
   * Creates dropdown options for number of items.
   */
  private createDropDownRowNoItems(): void {
    this.itemsPerPageOption = [8, 16, 24, 32].map((rows) => ({
      value: rows,
      label: String(rows),
    }));
  }

  setPreFilter() {
    this.filters.activeFilters = [];
    const filters: DisplayOptions = this.displayOptions
      ? this.displayOptions
      : this.preFilter;
    if (filters) {
      const keys = [
        'assetclass',
        'investmentteam',
        'investmenttheme',
        'publicationseries',
        'speciality',
      ];
      Object.entries(filters).forEach(([key, value]) => {
        if (keys.includes(key.toLowerCase())) {
          if (value) {
            const arr = [];
            arr.push({ value });
            if (this.displayOptions) {
              this.displayOptions.isPreFiltered = true;
            }
            if (key === 'assetClass' || key === 'assetclass') {
              this.filters.addFilter('assetclassPage.exact', arr);
            } else {
              this.filters.addFilter(key.toLowerCase() + '.exact', arr);
            }
          }
        }
      });
    }
  }

  termSearch(term: string) {
    if (term === '' || term === '*') {
      this.options.term = '*';
    } else {
      if (term.length > 3) {
        this.options.term = term;
        this.searchTerm = term;
      }
    }

    if (this.displayOptions?.articleType === 'press-release') {
      this.options.articleType = 'press-release';
    }

    this.options.page = 0;
    const searchParams: FtSearchParams = {
      options: this.options,
      filters: this.filters.getFilters(),
    };
    this.filtersAvailable = false;
    this.debouncer$.next(searchParams);
    return true;
  }

  onFiltered() {
    this.options.page = 0;
    if (this.paginationComponent) {
      this.paginationComponent.activePage = 1;
    }
    const searchParams = {
      options: this.options,
      filters: this.filters.getFilters(),
    };
    this.searchService.getInsights(searchParams);
    return true;
  }

  tabSelected(selectedButton: ToggleButton): void {
    this.selectedTab = selectedButton.value;
  }

  truncateString(str: string): string {
    if (str?.length > 200) {
      return str.slice(0, 200) + '...';
    }
    return str;
  }

  getImageUrl(widenObject: any) {
    if (widenObject) {
      return this.widenService.getWidenImageVariantUrl(
        JSON.parse(widenObject).url,
        'original',
        'webp',
        'eds-card-article'
      );
    }
  }

  getTranslationLabels() {
    logger.debug(this.component?.getModels());
    if (this.component?.getModels()['ft.core.insightsLibrary']) {
      return this.component?.getModels()['ft.core.insightsLibrary'];
    } else if (this.component?.getModels()['ft.core.closedEndPressReleases']) {
      return this.component?.getModels()['ft.core.closedEndPressReleases'];
    }
    if (this.externalTranslationLabel) {
      return this.externalTranslationLabel;
    }
  }

  goToPage(pageEvent) {
    if (pageEvent.page && pageEvent.action !== 'load') {
      this.options.page = (pageEvent.page - 1) * this.options.counters.pages;
      const searchParams = {
        options: this.options,
        filters: this.filters.getFilters(),
      };
      this.searchService.getInsights(searchParams);
      this.scrollService.scrollToAnchor('insightScrollPosition');
      return true;
    }
  }

  selectArticleType(event: number) {
    switch (event) {
      case 0:
        this.options.current = '';
        this.selectedArticleType = this.translationLabel?.allArticles;
        break;
      case 1:
        this.options.current = 'y';
        this.selectedArticleType = this.translationLabel?.currentArticles;
        break;
      case 2:
        this.options.current = 'n';
        this.selectedArticleType = this.translationLabel?.archivedArticles;
        break;
      default:
        this.options.current = 'y';
        this.selectedArticleType = this.translationLabel?.currentArticles;
        break;
    }

    this.analytics.trackEvent({
      event: 'dropdown_select',
      name: 'Articles Type',
      label: this.selectedArticleType,
    });

    if (this.paginationComponent) {
      this.paginationComponent.activePage = 1;
    }

    this.options.page = 0;
    const searchParams = {
      options: this.options,
      filters: this.filters.getFilters(),
    };
    this.searchService.getInsights(searchParams);
    return true;
  }

  /**
   * Triggered when someone changes the item per page option.
   */
  public onDropdownChange(numberOfRowsEvent): void {
    this.pageSize = this.itemsPerPageOption.find(
      (options) => Number(options.label) === numberOfRowsEvent.value
    );
    this.options.counters.pages = this.pageSize.value;
    if (this.paginationComponent) {
      this.paginationComponent.activePage = 1;
    }
    this.options.page = 0;
    const searchParams = {
      options: this.options,
      filters: this.filters.getFilters(),
    };
    this.searchService.getInsights(searchParams);
    this.scrollService.scrollToAnchor('insightScrollPosition');
  }

  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
